import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { PasswordInput } from "@/components/ui/password-input";
import { api } from "@/lib/api";
import { BTN_PAGE_PRIMARY_ACTION } from "@/lib/buttonStyles";
import {
  getPasswordConfirmationCheck,
  getPasswordRuleChecks,
  isPasswordValid,
} from "@/lib/passwordRules";
import { Check, Loader2 } from "lucide-react";
import { FormEvent, useMemo, useState } from "react";
import { toast } from "@/lib/appToast";

type AlterarSenhaPageProps = {
  embedded?: boolean;
};

/** Tom único de cinza: bolinha, textos de requisito e placeholders. */
const SENHA_HINT_TEXT = "text-gray-300";
const SENHA_HINT_DOT = "bg-gray-300";
const SENHA_HINT_PLACEHOLDER = "placeholder:!text-gray-300";

function PasswordRequirementItem({ met, label }: { met: boolean; label: string }) {
  return (
    <li className={`flex items-start gap-2 text-sm ${SENHA_HINT_TEXT}`}>
      {met ? (
        <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" aria-hidden />
      ) : (
        <span className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${SENHA_HINT_DOT}`} aria-hidden />
      )}
      <span className={SENHA_HINT_TEXT}>{label}</span>
    </li>
  );
}

export default function AlterarSenhaPage({ embedded = false }: AlterarSenhaPageProps) {
  const [senhaAntiga, setSenhaAntiga] = useState("");
  const [senhaNova, setSenhaNova] = useState("");
  const [confirmacao, setConfirmacao] = useState("");
  const [saving, setSaving] = useState(false);

  const passwordRules = useMemo(() => getPasswordRuleChecks(senhaNova), [senhaNova]);
  const confirmationRule = useMemo(
    () => getPasswordConfirmationCheck(senhaNova, confirmacao),
    [senhaNova, confirmacao],
  );

  const allRequirementsMet =
    senhaAntiga.trim().length > 0 &&
    isPasswordValid(senhaNova) &&
    confirmationRule.met;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!allRequirementsMet || saving) return;

    try {
      setSaving(true);
      await api.alterarSenha({ senhaAtual: senhaAntiga, senhaNova });
      setSenhaAntiga("");
      setSenhaNova("");
      setConfirmacao("");
      toast.success("Senha alterada com sucesso.");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Não foi possível alterar a senha.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={embedded ? "w-full" : "mx-auto max-w-3xl"}>
      <Card className="border border-gray-200 bg-white shadow-sm">
        <CardContent className="space-y-6 !p-6 sm:!p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="senha-antiga">Senha Antiga</Label>
                <PasswordInput
                  id="senha-antiga"
                  value={senhaAntiga}
                  onChange={(e) => setSenhaAntiga(e.target.value)}
                  placeholder="Senha Antiga"
                  autoComplete="current-password"
                  required
                  className={SENHA_HINT_PLACEHOLDER}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="senha-nova">Senha Nova</Label>
                <PasswordInput
                  id="senha-nova"
                  value={senhaNova}
                  onChange={(e) => setSenhaNova(e.target.value)}
                  placeholder="Senha Nova"
                  autoComplete="new-password"
                  required
                  className={SENHA_HINT_PLACEHOLDER}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="senha-confirmacao">Confirme a Nova Senha</Label>
                <PasswordInput
                  id="senha-confirmacao"
                  value={confirmacao}
                  onChange={(e) => setConfirmacao(e.target.value)}
                  placeholder="Confirme a Nova Senha"
                  autoComplete="new-password"
                  required
                  className={SENHA_HINT_PLACEHOLDER}
                />
              </div>
            </div>

            <ul className="space-y-2" aria-live="polite">
              {passwordRules.map((rule) => (
                <PasswordRequirementItem key={rule.id} met={rule.met} label={rule.label} />
              ))}
              <PasswordRequirementItem met={confirmationRule.met} label={confirmationRule.label} />
            </ul>

            {allRequirementsMet ? (
              <div className="flex justify-end pt-2">
                <Button type="submit" className={BTN_PAGE_PRIMARY_ACTION} disabled={saving}>
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                  Salvar
                </Button>
              </div>
            ) : null}
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
